import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { CREDENTIALS, BRAND } from "@/lib/content";

const VALUES = [
  {
    n: "01",
    title: "Independence",
    body: "No agency relationships, no kickbacks, no shortcuts. Just an honest professional opinion you can act on.",
  },
  {
    n: "02",
    title: "Rigour",
    body: "Methodical inspection, accredited tools, and reports written to a standard that holds up under scrutiny.",
  },
  {
    n: "03",
    title: "Clarity",
    body: "Plain-English commentary instead of jargon. Findings explained so you know what to do next.",
  },
  {
    n: "04",
    title: "Care",
    body: "Working with a small number of clients at any one time, so every project gets the attention it deserves.",
  },
];

export default function About() {
  return (
    <SiteLayout
      pageTitle="About"
      pageDescription="Declan Bishop is an independent, triple-accredited property consultant (AssocRICS · MCIOB · BDMA.Tech) serving homeowners, landlords, buyers and estate agents throughout Lincolnshire and the surrounding areas."
      pagePath="/about"
    >
      {/* Header */}
      <section data-testid="about-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid grid-cols-12 gap-y-10 md:gap-x-12">
            <div className="col-span-12 md:col-span-7">
              <div className="overline" data-testid="about-overline">About the practice</div>
              <h1
                data-testid="about-headline"
                className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight"
              >
                A property consultancy
                <br />
                <span className="italic font-light text-brand-champagne">built on craft</span>
                <span className="italic font-light"> and quiet rigour.</span>
              </h1>
              <p className="mt-10 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
                Declan Bishop is an independent property consultant working throughout
                Lincolnshire and the surrounding areas. The practice was founded on a simple
                idea: that the people who pay for a survey deserve to understand it — and to
                trust the person who wrote it.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end">
              <div className="border border-brand-navy/10 p-10 bg-white max-w-sm">
                <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
                  At a glance
                </div>
                <ul className="mt-6 space-y-5 text-sm text-brand-navy/80">
                  <li className="flex justify-between gap-6">
                    <span className="text-brand-navy/55 uppercase tracking-[0.16em] text-[11px]">Founded</span>
                    <span>Independent practice</span>
                  </li>
                  <li className="flex justify-between gap-6">
                    <span className="text-brand-navy/55 uppercase tracking-[0.16em] text-[11px]">Coverage</span>
                    <span>Lincolnshire &amp; surrounding</span>
                  </li>
                  <li className="flex justify-between gap-6">
                    <span className="text-brand-navy/55 uppercase tracking-[0.16em] text-[11px]">Practice</span>
                    <span>Full-service</span>
                  </li>
                  <li className="flex justify-between gap-6">
                    <span className="text-brand-navy/55 uppercase tracking-[0.16em] text-[11px]">Accreditations</span>
                    <span>AssocRICS · MCIOB · BDMA.Tech</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience narrative */}
      <section data-testid="about-story" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-24 md:py-32">
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
            <div className="col-span-12 md:col-span-4">
              <div className="overline">Experience</div>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                Over fifteen years inside the construction and surveying industry.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 text-base md:text-lg text-brand-navy/75 leading-relaxed">
              <p>
                Declan trained as a building professional before specialising in surveying,
                damage diagnostics and domestic energy. That hands-on background means every
                report is written with a real understanding of how buildings are put together —
                and how they fail.
              </p>
              <p>
                The practice serves homeowners deciding whether to buy, landlords protecting
                their portfolios, families taking handover of a new build, and estate agents
                who need a calm, independent voice when a transaction gets complicated.
              </p>
              <p>
                What clients return for, again and again, is the rare combination of formal
                accreditation, working-site experience, and the willingness to explain the
                findings in language they can actually use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials deep */}
      <section data-testid="about-credentials" className="bg-brand-cream">
        <div className="container-narrow py-24 md:py-32">
          <div className="overline">Accreditations & Memberships</div>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl text-brand-navy leading-tight tracking-tight max-w-3xl">
            Triple-accredited across surveying, construction and damage diagnostics.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {CREDENTIALS.map((c) => (
              <div
                key={c.code}
                data-testid={`about-credential-${c.code}`}
                className="bg-brand-cream p-10"
              >
                <div className="font-serif text-5xl md:text-6xl text-brand-navy tracking-tight">
                  {c.code}
                </div>
                <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-brand-champagne font-semibold">
                  {c.label}
                </div>
                <p className="mt-6 text-sm text-brand-navy/70 leading-relaxed">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="bg-brand-navy text-brand-cream">
        <div className="container-narrow py-24 md:py-32">
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
            <div className="col-span-12 md:col-span-5">
              <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
                Approach
              </div>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">
                Four principles
                <br />
                <span className="italic font-light text-brand-champagne">that shape every report.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-cream/10">
              {VALUES.map((v) => (
                <div
                  key={v.n}
                  data-testid={`about-value-${v.n}`}
                  className="bg-brand-navy p-8 md:p-10"
                >
                  <div className="font-serif text-3xl text-brand-champagne">{v.n}</div>
                  <h3 className="mt-4 font-serif text-2xl">{v.title}</h3>
                  <p className="mt-3 text-sm text-brand-cream/70 leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Confidence band */}
      <section data-testid="about-confidence" className="bg-white border-t border-brand-navy/10">
        <div className="container-narrow py-24 md:py-32 text-center">
          <div className="overline">Client Confidence</div>
          <p className="mt-8 mx-auto max-w-3xl font-serif italic text-2xl md:text-3xl text-brand-navy leading-snug">
            &ldquo;The job of a property consultant is to take a complicated building and hand a
            calm, honest summary back to the person who has to make the decision.&rdquo;
          </p>
          <div className="mt-10 text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
            — Declan Bishop, AssocRICS · MCIOB · BDMA.Tech
          </div>

          <div className="mt-20 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="about-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Start an Enquiry
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="about-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call the practice
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
